import styles from './companyPanel.module.css'
import { toNumber } from '../../../../utils/typeUtils'
import { useSystemState, useSystemStateHooks } from '../../../system'
import { useActions } from './actions'
import { useManagerState } from '../../../manager'
import { DetailFieldProps, FieldType, FieldValue, FormField, FormatType, ImageField, ProfilePanel, Spinner, UIPermission, Link, O, Button, ModalId, showErrorStrToast, useUIActions } from '../../../../features/ui'
import { useEffect, useMemo, useState } from 'react'
import { useFileActions } from '../../../../features/files'
import { Data, Data2, useDataTypesHooks, validatePhone } from '../../../../features/data-types'
import { AddressFormModal, addressHelpers } from '../../../../features/address'
import { SalePoint } from '../../../system/state/types'
import { TaxIdsTable } from './taxIdsTable'
import { TaxIdFormModal } from './taxIdFormModal'

// 
export const CompanyPanel = () => {
  const { branches } = useManagerState()
  const { company } = useSystemState()

  const [asyncDataLoaded, setAsyncDataLoaded] = useState(!company!.logoUrl)
  const [logoFile, setLogoFile] = useState<File>()
  const [addressData, setAddressData] = useState<Data2>(company!.address)
  const [taxIdsData, setTaxIdsData] = useState<{ taxId: number; main: boolean; salePoint?: SalePoint }[]>(company!.taxIds)
  const [currentTaxId, setCurrentTaxId] = useState<O<number>>()
  const [currentIsMain, setCurrentIsMain] = useState(false)

  const { describeAddress } = addressHelpers
  const { userHasPermission } = useSystemStateHooks()
  const readOnly = !userHasPermission(UIPermission.SAVE_COMPANY)
  const { formatCustomValue } = useDataTypesHooks()

  const { onBranchClick, updateCompany } = useActions()
  const { fetchFile } = useFileActions()
  const { openModal } = useUIActions()

  useEffect(() => {
    if (company!.logoUrl) {
      fetchFile(company!.logoUrl, true).then(file => {
        setLogoFile(file)
        setAsyncDataLoaded(true)
      })
    } else {
      setAsyncDataLoaded(true)
    }
  }, [])

  const companyData = useMemo(() => {
    const { address, taxIds, ...data } = company!
    const mainTaxId = taxIds.find(t => t.main)?.taxId
    return {
      ...data,
      taxId: mainTaxId,
      address: describeAddress(address),
      imageUrl: company!.logoUrl
    }
  }, [company])

  const onEditAddress = () => openModal(ModalId.ADDRESS_FORM)

  const onAddTaxId = () => {
    setCurrentTaxId(undefined)
    openModal(ModalId.COMPANY_TAX_IDS)
  }

  const onEditTaxId = (taxIdStr: string) => {
    const taxId = toNumber(taxIdStr)
    const row = taxIdsData.find(t => t.taxId === taxId)
    setCurrentTaxId(taxId)
    setCurrentIsMain(row?.main ?? false)
    openModal(ModalId.COMPANY_TAX_IDS)
  }

  const onRemoveTaxId = (taxIdStr: string) => {
    const taxId = toNumber(taxIdStr)
    const row = taxIdsData.find(t => t.taxId === taxId)
    if (row?.main) {
      showErrorStrToast('No se puede eliminar el CUIT principal')
      return
    }
    if (row?.salePoint) {
      showErrorStrToast('No se puede eliminar un CUIT que tiene un punto de venta asociado')
      return
    }
    setTaxIdsData(prev => prev.filter(t => t.taxId !== taxId))
  }

  const setMainTaxId = (
    rows: { taxId: number; main: boolean }[],
    taxId: number
  ) =>
    rows.map(t => ({
      ...t,
      main: t.taxId === taxId
    }))

  const onSaveTaxId = (taxId: number, isMain: boolean) => {
    setTaxIdsData(prev => {
      const isEdit = currentTaxId !== undefined
      const rows = isEdit
        ? prev.map(t =>
          t.taxId === currentTaxId
            ? { ...t, taxId, main: isMain }
            : t
        )
        : [{ taxId, main: isMain }, ...prev]

      return isMain ? setMainTaxId(rows, taxId) : rows
    })

    setCurrentTaxId(undefined)
    setCurrentIsMain(false)
  }

  const infoFields: DetailFieldProps[] = [
    {
      name: 'legalName',
      type: FieldType.TEXT,
      label: 'Razón Social'
    },
    {
      name: 'taxId',
      type: FieldType.TEXT,
      label: 'CUIT',
      render: (value?: FieldValue) => {
        const taxId = value! as number
        return <>{formatCustomValue(taxId, FormatType.CUIT_CUIL)}</>
      }
    },
    {
      name: 'address',
      type: FieldType.TEXT,
      label: 'Dirección'
    },
    {
      name: 'email',
      type: FieldType.TEXT,
      label: 'Email'
    }
  ]
  if (company!.phone) {
    const phoneField = {
      name: 'phone',
      type: FieldType.NUMBER,
      label: 'Teléfono',
      render: (value?: FieldValue) => {
        const phone = value as O<number>
        return <>{formatCustomValue(phone, FormatType.PHONE)}</>
      }
    }
    infoFields.push(phoneField)
  }
  if (company!.websiteUrl) {
    const websiteUrlField = {
      name: 'websiteUrl',
      type: FieldType.TEXT,
      label: 'Página Web',
      render: (value?: FieldValue) => {
        const websiteUrl = value! as string
        return <Link
          text={websiteUrl}
          href={`https://${websiteUrl}`}
          kind="primary"
          className={styles.link}
        />
      }
    }
    infoFields.splice(2, 0, websiteUrlField)
  }
  if (company!.grossIncomeCode) {
    const grossIncomeCodeField = {
      name: 'grossIncomeCode',
      type: FieldType.NUMBER,
      label: 'N° de Inscripción a IIBB'
    }
    infoFields.push(grossIncomeCodeField)
  }
  const branchesField = {
    name: 'branches',
    type: FieldType.TEXT,
    label: 'Sucursales',
    render: () => <div className={styles.branchesContainer}>
      {branches.map(branch => <Link
        key={branch.name}
        text={branch.name}
        kind="primary"
        className={styles.link}
        onClick={onBranchClick(branch)}
      />)}
    </div>
  }
  infoFields.push(branchesField)

  const renderAsync = (element: JSX.Element) => asyncDataLoaded ? element : <Spinner size='m'/>

  const fields: FormField[] = [
    {
      name: 'legalName',
      type: FieldType.TEXT,
      label: 'Razón Social'
    },
    {
      name: 'taxIds',
      type: FieldType.TABLE,
      label: 'CUITs',
      render: () => (
        <TaxIdsTable
          rows={taxIdsData}
          onCreate={onAddTaxId}
          onEditRow={onEditTaxId}
          onRemoveRow={onRemoveTaxId}
        />
      )
    },
    {
      name: 'logoUrl',
      type: FieldType.IMAGE,
      label: 'Logo',
      render: () => renderAsync(<ImageField
        id="logoUrl"
        value={logoFile}
        onChange={setLogoFile}
      />)
    },
    {
      name: 'websiteUrl',
      type: FieldType.TEXT,
      label: 'Página Web'
    },
    {
      name: 'address',
      type: FieldType.TEXT,
      label: 'Dirección',
      render: () => (
        <div className={styles.editableField}>
          <span>{describeAddress(addressData)}</span>
          <Button
            icon="pencil-fill"
            kind="tertiary"
            small
            onClick={onEditAddress}
          />
        </div>
      )
    },
    {
      name: 'email',
      type: FieldType.TEXT,
      label: 'Email'
    },
    {
      name: 'phone',
      type: FieldType.NUMBER,
      label: 'Teléfono',
      validate: validatePhone
    },
    {
      name: 'grossIncomeCode',
      type: FieldType.NUMBER,
      label: 'N° de Inscripción a IIBB'
    }
  ]

  const onSubmit = async (data: Data) => {
    return updateCompany(company!, data, addressData as Data, taxIdsData, logoFile)
  }

  return (
    <>
      <ProfilePanel
        infoFields={infoFields}
        fields={fields}
        data={companyData}
        subtitle={company!.websiteUrl}
        infoTabLabel='Información de Compañía'
        readOnly={readOnly}
        onSubmit={onSubmit}
        onCancelEdit={() => setTaxIdsData([...company!.taxIds])}
      />
      <AddressFormModal
        addressData={addressData}
        onSubmit={setAddressData}
      />
      <TaxIdFormModal
        data={currentTaxId !== undefined ? { taxId: currentTaxId, main: currentIsMain } : undefined}
        isMain={currentIsMain}
        onSubmit={onSaveTaxId}
      />
    </>
  )
}
